import { useCallback, useMemo, useRef, useState } from "react"
import type { UIMessage } from "@ai-sdk/react"
import { useChat } from "@ai-sdk/react"
import { DefaultChatTransport } from "ai"

import type { OutfitterAssistantAgent } from "~components/outfitter-assistant/outfitter-assistant-constants"
import { accessTokenVar, getAccessTokenFuncVar } from "~utils/hooks/use-auth"
import type { AiAgent } from "~graphql/generated/types"
import { getDirectRestApiUrl } from "~utils/helpers/helpers"
import { instrumentedFetch } from "~utils/sentry"

const AI_CHAT_ENDPOINT = "webhooks/ai/chat"
export const CHAT_STATUSES = {
    READY: "ready",
    SUBMITTED: "submitted",
    STREAMING: "streaming",
    ERROR: "error",
}
const AI_CHAT_OPERATION_NAME = "ai.chat"
const CONVERSATION_ID_HEADER = "x-ai-conversation-id"
const IN_FLIGHT_CHAT_STATUSES: readonly string[] = [
    CHAT_STATUSES.SUBMITTED,
    CHAT_STATUSES.STREAMING,
]
const AUTHORIZATION_HEADER = "authorization"

interface Props {
    readonly aiAgent: AiAgent | OutfitterAssistantAgent
    readonly context: Record<string, unknown>
    /**
     * Merged on top of `context` at send time. Use for values that are
     * expensive to compute on every render (e.g. DOM snapshots) or that must
     * be captured synchronously when the user actually sends.
     */
    readonly getDynamicContext?: () => Record<string, unknown>
    /**
     * Runs after a send (or retry) completes successfully. Used to
     * checkpoint send-scoped state — e.g. clearing a breadcrumb trail once
     * the model has received and processed it. Not called on failure so
     * retries keep the same context.
     */
    readonly onAfterSend?: () => void
}

export default function useAiChat({
    aiAgent,
    context,
    getDynamicContext,
    onAfterSend,
}: Props) {
    const [conversationId, setConversationId] = useState<string | null>(null)
    const { advance, versionRef } = useConversationVersion()
    const transport = useAiChatTransport({
        conversationVersionRef: versionRef,
        setConversationId,
    })
    const {
        messages,
        sendMessage: sendChatMessage,
        regenerate: regenerateChat,
        setMessages,
        clearError,
        stop,
        status,
        error,
    } = useChat({ transport })
    const isLoading = IN_FLIGHT_CHAT_STATUSES.includes(status)

    const buildChatRequestBody = useBuildChatRequestBody({
        aiAgent,
        context,
        conversationId,
        getDynamicContext,
    })

    const loadConversation = useLoadConversation({
        advanceConversationVersion: advance,
        clearError,
        setConversationId,
        setMessages,
        stop,
        status,
    })
    const startNewConversation = useStartNewConversation({
        advanceConversationVersion: advance,
        clearError,
        setConversationId,
        setMessages,
        stop,
        status,
    })

    const sendMessage = useCallback<typeof sendChatMessage>(
        (message, options) => {
            // Clear any prior error so the chat transitions out of the
            // "error" status and accepts the new send.
            if (status === CHAT_STATUSES.ERROR) clearError()

            const pending = sendChatMessage(message, {
                ...options,
                body: buildChatRequestBody(options?.body),
            })
            notifyOnSuccess(pending, onAfterSend)
            return pending
        },
        [sendChatMessage, clearError, status, buildChatRequestBody, onAfterSend]
    )

    const retry = useCallback<typeof regenerateChat>(
        (options) => {
            if (status === CHAT_STATUSES.ERROR) clearError()
            const pending = regenerateChat({
                ...options,
                body: buildChatRequestBody(options?.body),
            })
            notifyOnSuccess(pending, onAfterSend)
            return pending
        },
        [regenerateChat, clearError, status, buildChatRequestBody, onAfterSend]
    )

    return useAiChatValue({
        messages,
        sendMessage,
        retry,
        isLoading,
        error,
        conversationId,
        loadConversation,
        startNewConversation,
    })
}

function useConversationVersion() {
    const conversationVersionRef = useRef(0)
    const advanceConversationVersion = useCallback(() => {
        conversationVersionRef.current += 1
    }, [])

    return {
        advance: advanceConversationVersion,
        versionRef: conversationVersionRef,
    }
}

function useBuildChatRequestBody({
    aiAgent,
    context,
    conversationId,
    getDynamicContext,
}: {
    readonly aiAgent: AiAgent | OutfitterAssistantAgent
    readonly context: Record<string, unknown>
    readonly conversationId: string | null
    readonly getDynamicContext?: () => Record<string, unknown>
}) {
    return useCallback(
        (existingBody: object | undefined) => {
            const dynamic = getDynamicContext?.() ?? {}
            return {
                ...(existingBody ?? {}),
                aiAgent,
                ...(conversationId ? { conversationId } : {}),
                context: { ...context, ...dynamic },
            }
        },
        [aiAgent, context, conversationId, getDynamicContext]
    )
}

function useAiChatValue({
    messages,
    sendMessage,
    retry,
    isLoading,
    error,
    conversationId,
    loadConversation,
    startNewConversation,
}: {
    readonly messages: UIMessage[]
    readonly sendMessage: ReturnType<typeof useChat>["sendMessage"]
    readonly retry: ReturnType<typeof useChat>["regenerate"]
    readonly isLoading: boolean
    readonly error: Error | undefined
    readonly conversationId: string | null
    readonly loadConversation: (params: {
        readonly conversationId: string
        readonly messages: UIMessage[]
    }) => void
    readonly startNewConversation: () => void
}) {
    return useMemo(
        () => ({
            messages,
            sendMessage,
            retry,
            isLoading,
            error,
            conversationId,
            loadConversation,
            startNewConversation,
        }),
        [
            messages,
            sendMessage,
            retry,
            isLoading,
            error,
            conversationId,
            loadConversation,
            startNewConversation,
        ]
    )
}

function useLoadConversation({
    advanceConversationVersion,
    clearError,
    setConversationId,
    setMessages,
    stop,
    status,
}: {
    readonly advanceConversationVersion: () => void
    readonly clearError: () => void
    readonly setConversationId: (conversationId: string) => void
    readonly setMessages: (messages: UIMessage[]) => void
    readonly stop: () => void
    readonly status: string
}) {
    return useCallback(
        ({
            conversationId: nextConversationId,
            messages: nextMessages,
        }: {
            readonly conversationId: string
            readonly messages: UIMessage[]
        }) => {
            if (status === CHAT_STATUSES.ERROR) clearError()
            if (IN_FLIGHT_CHAT_STATUSES.includes(status)) stop()
            advanceConversationVersion()
            setConversationId(nextConversationId)
            setMessages(nextMessages)
        },
        [
            advanceConversationVersion,
            clearError,
            setConversationId,
            setMessages,
            status,
            stop,
        ]
    )
}

function useStartNewConversation({
    advanceConversationVersion,
    clearError,
    setConversationId,
    setMessages,
    stop,
    status,
}: {
    readonly advanceConversationVersion: () => void
    readonly clearError: () => void
    readonly setConversationId: (conversationId: string | null) => void
    readonly setMessages: (messages: UIMessage[]) => void
    readonly stop: () => void
    readonly status: string
}) {
    return useCallback(() => {
        if (status === CHAT_STATUSES.ERROR) clearError()
        if (IN_FLIGHT_CHAT_STATUSES.includes(status)) stop()
        advanceConversationVersion()
        setConversationId(null)
        setMessages([])
    }, [
        advanceConversationVersion,
        clearError,
        setConversationId,
        setMessages,
        status,
        stop,
    ])
}

function useAiChatTransport({
    conversationVersionRef,
    setConversationId,
}: {
    readonly conversationVersionRef: { readonly current: number }
    readonly setConversationId: (conversationId: string) => void
}) {
    // Resolve the auth header lazily on every send so Auth0 can transparently
    // refresh an expired JWT (matches the Apollo authLink pattern). Reading
    // `accessTokenVar` directly would ship the last cached token forever,
    // which is why expired-token errors keep bubbling up mid-session.
    return useMemo(
        () =>
            new DefaultChatTransport({
                api: `${getDirectRestApiUrl()}${AI_CHAT_ENDPOINT}`,
                headers: resolveAuthorizationHeader,
                fetch: (input, init) => {
                    const conversationVersion = conversationVersionRef.current
                    return fetchWithConversationId({
                        conversationVersion,
                        conversationVersionRef,
                        input,
                        init,
                        setConversationId,
                    })
                },
            }),
        [conversationVersionRef, setConversationId]
    )
}

async function fetchWithConversationId({
    conversationVersion,
    conversationVersionRef,
    input,
    init,
    setConversationId,
}: {
    readonly conversationVersion: number
    readonly conversationVersionRef: { readonly current: number }
    readonly input: RequestInfo | URL
    readonly init?: RequestInit
    readonly setConversationId: (conversationId: string) => void
}) {
    const response = await instrumentedFetch(input, init, {
        operationName: AI_CHAT_OPERATION_NAME,
    })
    const nextConversationId = response.headers.get(CONVERSATION_ID_HEADER)
    if (
        nextConversationId &&
        conversationVersion === conversationVersionRef.current
    ) {
        setConversationId(nextConversationId)
    }

    return response
}

function notifyOnSuccess(
    pending: Promise<unknown>,
    callback: (() => void) | undefined
): void {
    if (!callback) return
    void callAfterSuccess({ pending, callback })
}

async function callAfterSuccess({
    pending,
    callback,
}: {
    pending: Promise<unknown>
    callback: () => void
}): Promise<void> {
    // Swallow rejection — errors are surfaced through the chat hook's
    // `error` status, and we must not run the success callback when the
    // send failed (otherwise retries lose send-scoped context like the
    // navigation breadcrumb trail).
    try {
        await pending
        callback()
    } catch {
        // No-op; the chat hook exposes the error state.
    }
}

// Called by the AI SDK transport on every outgoing chat request. We prefer
// the Auth0 getter (which refreshes the JWT when expired); the cached
// `accessTokenVar` is only used as a fallback if the getter has not been
// registered yet (e.g. a send fires before `useAuth` mounted).
async function resolveAuthorizationHeader(): Promise<Record<string, string>> {
    const getAccessToken = getAccessTokenFuncVar()
    const accessToken = (await getAccessToken?.()) ?? accessTokenVar() ?? ""
    return { [AUTHORIZATION_HEADER]: `Bearer ${accessToken}` }
}
